import React, { forwardRef, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  ButtonGroup,
  FormGroup,
  InputGroup,
  Radio,
  RadioGroup,
  Label,
  Tabs,
  Tab,
  Callout,
  Intent,
} from '@blueprintjs/core';
import produce from 'utils/produce';
import { Grid } from 'components/base';
import classNames from 'classnames';

import imageApi from '../../api/image';
import EventDateInput from './eventDateInput';
import PlaceAutoCompleteInput from './placeAutoCompleteInput';
import PersonSelect from '../../components/ancestry/personSelect';

export const DEFAULT_PERSON = {
  partners: [],
  gender: 'unknown',
  status: 'living',
};

const formatDateObject = ({ d, m, y } = {}) => {
  if (y) {
    return `${y}`;
  }

  return undefined;
};

const EditPersonModal = forwardRef((props, ref) => {
  const [selectedTabId, setSelectedTabId] = useState('profile');
  const [state, setState] = useState({ ...DEFAULT_PERSON, ...props.person });

  const people = useMemo(() => {
    return props.people.reduce((acc, p) => {
      if (p.id !== props.person.id) {
        acc.push({
          ...p,
          birth: formatDateObject(p.birthDate),
          death: formatDateObject(p.deathDate),
        });
      }
      return acc;
    }, []);
  }, [props.person, props.people]);

  const update = (key, value) => {
    setState(val =>
      produce(val, draft => {
        draft[key] = value;
      }),
    );
  };

  const updateMarriage = (pid, key, value) => {
    setState(val =>
      produce(val, draft => {
        const marriage = {
          ...val.marriage[pid],
          [key]: value,
        };

        // update person marriage
        draft.marriage[pid] = marriage;

        // update partner marriage
        const partnerIndex = val.partners.findIndex(partner => partner.id === pid);
        if (partnerIndex >= 0) {
          draft.partners[partnerIndex].marriage[val.id] = marriage;
        }
      }),
    );
  };

  useEffect(() => {
    const value = {
      ...state,
      // TODO photos
      photos: state.photo ? [{ type: 'a', url: state.photo }] : [],
    };

    if (ref) {
      ref.current = value;
    }
    if (props.onChange) {
      props.onChange(value);
    }
  }, [state, props.onChange]);

  const handleOnAddPhotoClick = e => {
    e.preventDefault();

    props.uploader.choosePhotos(1, async newPhoto => {
      const { data, error } = await imageApi(props.accessToken).uploadImage(newPhoto.url);
      if (!error) {
        update('photo', data.publicUrl);
      }
    });
  };

  const handleOnRemovePhotoClick = e => {
    update('photo', undefined);
  };

  return (
    <Tabs id="tabs" animate={false} selectedTabId={selectedTabId} onChange={setSelectedTabId}>
      <Tab
        id="profile"
        title="Profile"
        panel={
          <form autoComplete="off">
            <Grid.Row>
              <Grid.Col xs={9}>
                <Grid.Row>
                  <Grid.Col xs={6}>
                    <FormGroup labelFor="firstName" label="First Name">
                      <InputGroup
                        id="firstName"
                        placeholder="First Name"
                        value={state.first}
                        onChange={e => update('first', e.target.value)}
                      />
                    </FormGroup>
                  </Grid.Col>
                  <Grid.Col xs={6}>
                    <FormGroup labelFor="lastName" label="Last Name">
                      <InputGroup
                        id="lastName"
                        placeholder="Last Name"
                        value={state.last}
                        onChange={e => update('last', e.target.value)}
                      />
                    </FormGroup>
                  </Grid.Col>
                </Grid.Row>
              </Grid.Col>
              <Grid.Col xs={3}>
                <FormGroup labelFor="suffix" label="Suffix">
                  <InputGroup
                    id="suffix"
                    placeholder="Suffix"
                    value={state.suffix}
                    onChange={e => update('suffix', e.target.value)}
                  />
                </FormGroup>
              </Grid.Col>
            </Grid.Row>

            <Grid.Row>
              <Grid.Col xs={6}>
                <RadioGroup
                  label="Gender"
                  onChange={e => update('gender', e.target.value)}
                  selectedValue={state.gender}
                  inline
                >
                  <Radio label="Male" value="male" />
                  <Radio label="Female" value="female" />
                  <Radio label="Unknown" value="unknown" />
                </RadioGroup>
              </Grid.Col>
              <Grid.Col xs={6}>
                <RadioGroup
                  label="Status"
                  onChange={e => update('status', e.target.value)}
                  selectedValue={state.status}
                  inline
                >
                  <Radio label="Deceased" value="deceased" />
                  <Radio label="Living" value="living" />
                </RadioGroup>
              </Grid.Col>
            </Grid.Row>

            <Grid.Row className="mt-1">
              <Grid.Col xs={3}>
                <Label>Events</Label>
              </Grid.Col>
              <Grid.Col xs={6}>
                <Label>Place</Label>
              </Grid.Col>
              <Grid.Col xs={3}>
                <Label>Date</Label>
              </Grid.Col>
            </Grid.Row>

            <Grid.Row className="mb-1">
              <Grid.Col xs={3}>
                <Label className="mb-0">Birth</Label>
              </Grid.Col>
              <Grid.Col xs={6}>
                <PlaceAutoCompleteInput
                  id="birthPlace"
                  placeholder="Birth Place"
                  value={state.birthPlace}
                  onChange={value => update('birthPlace', value)}
                />
              </Grid.Col>
              <Grid.Col xs={3}>
                <EventDateInput
                  value={state.birthDate}
                  onChange={val => update('birthDate', val)}
                />
              </Grid.Col>
            </Grid.Row>

            {state.partners.map(partner => (
              <Grid.Row key={partner.id} className="mb-1">
                <Grid.Col xs={3}>
                  <Label className="mb-0 text-truncate">
                    Marriage ({[partner.first, partner.last].filter(i => !!i).join(' ')})
                  </Label>
                </Grid.Col>
                <Grid.Col xs={6}>
                  <PlaceAutoCompleteInput
                    id={`marriagePlace-${partner.id}`}
                    placeholder="Marriage Place"
                    value={state.marriage[partner.id]?.place}
                    onChange={value => updateMarriage(partner.id, 'place', value)}
                  />
                </Grid.Col>
                <Grid.Col xs={3}>
                  <EventDateInput
                    value={state.marriage[partner.id]?.date}
                    onChange={value => updateMarriage(partner.id, 'date', value)}
                  />
                </Grid.Col>
              </Grid.Row>
            ))}

            <Grid.Row className={classNames('mb-1', { 'd-none': state.status === 'living' })}>
              <Grid.Col xs={3}>
                <Label className="mb-0">Death</Label>
              </Grid.Col>
              <Grid.Col xs={6}>
                <PlaceAutoCompleteInput
                  id="deathPlace"
                  placeholder="Death Place"
                  value={state.deathPlace}
                  onChange={value => update('deathPlace', value)}
                />
              </Grid.Col>
              <Grid.Col xs={3}>
                <EventDateInput
                  value={state.deathDate}
                  onChange={val => update('deathDate', val)}
                />
              </Grid.Col>
            </Grid.Row>
          </form>
        }
      />

      {props.type === 'edit' && (
        <Tab
          id="photos"
          title="Photos"
          panel={
            <>
              <Grid.Row>
                <Grid.Col xs={3}>
                  <Label className="mb-0">Profile Photo</Label>
                </Grid.Col>
                <Grid.Col xs={6}>
                  <ButtonGroup>
                    <Button disabled={props.uploader.isLoading} onClick={handleOnAddPhotoClick}>
                      {state.photo ? 'Change Photo' : 'Add Photo'}
                    </Button>
                    {state.photo && (
                      <Button
                        disabled={props.uploader.isLoading}
                        onClick={handleOnRemovePhotoClick}
                      >
                        Remove Photo
                      </Button>
                    )}
                  </ButtonGroup>
                  {state.photo && <img src={state.photo} className="mt-1 d-block w-100" />}
                </Grid.Col>
              </Grid.Row>
            </>
          }
        />
      )}

      {props.type === 'edit' && (
        <Tab
          id="relations"
          title="Relations"
          panel={
            <>
              <Callout intent={Intent.WARNING}>
                Please be careful, you may break your family tree by changing the relations. It is
                recommended to use a standard way of adding relations. Use this only in case you
                would like to change some previous mistake.
              </Callout>
              <Grid.Row className="mt-1">
                <Grid.Col xs={6}>
                  <Label>Father</Label>
                  <PersonSelect
                    fill
                    onChange={person => update('fid', person?.id ?? undefined)}
                    persons={people}
                    selectedPerson={people.find(p => p.id === state.fid)}
                    placeholder="Select father"
                    labelKey="fullName"
                  />
                </Grid.Col>

                <Grid.Col xs={6}>
                  <Label>Mother</Label>
                  <PersonSelect
                    fill
                    onChange={person => update('mid', person?.id ?? undefined)}
                    persons={people}
                    selectedPerson={people.find(p => p.id === state.mid)}
                    placeholder="Select mother"
                    labelKey="fullName"
                  />
                </Grid.Col>
              </Grid.Row>
            </>
          }
        />
      )}
    </Tabs>
  );
});

EditPersonModal.propTypes = {
  type: PropTypes.oneOf(['create', 'edit']),
  person: PropTypes.object,
  people: PropTypes.array,
  uploader: PropTypes.object,
  accessToken: PropTypes.string,
  onChange: PropTypes.func,
};

EditPersonModal.defaultProps = {
  people: [],
  type: 'edit',
};

export default EditPersonModal;
